import { Box, Stack, Typography, useTheme } from "@mui/material";
import { ChartType } from "enums/report";
import { ThemeMode } from "enums/theme";
import { AxisMapped, ReportDataDto } from "interfaces/report";
import { debounce, isFunction, max } from "lodash";
import { ColorPicker } from "material-ui-color";
import { useMemo } from "react";
import {convertStringToNumber, getTextColor} from "utils/string";
import numeral from "numeral";

interface Props {
  isOwner: boolean;
  chartType?: ChartType;
  reportData: ReportDataDto<AxisMapped>;
  sheetName: string;
  handleChangeXColor?: (key: string, color: string) => void;
  handleChangeYColor?: (key: string, color: string) => void;
  handleChangeSheetName?: Function;
  handleChangeChartType?: Function;
  handleSelectRow?: (key: string) => void;
  rotate?: boolean;
}

export default function ReportTable({
  isOwner,
  chartType,
  reportData,
  sheetName,
  handleChangeXColor,
  handleChangeYColor,
  rotate,
  handleSelectRow,
}: Props) {
  const theme = useTheme();
  const sheet = useMemo(() => {
    return reportData[sheetName];
  }, [reportData, sheetName]);

  const maxSheetLength = useMemo(() => {
    return (max(sheet.data.map((items) => items.length)) as number) - 1;
  }, [sheet]);

  const debouncedHandleChangeXColor = debounce(
    (key, color) =>
      isFunction(handleChangeXColor) && handleChangeXColor(key, color),
    250
  );

  const debouncedHandleChangeYColor = debounce(
    (key, color) =>
      isFunction(handleChangeYColor) && handleChangeYColor(key, color),
    250
  );

  const canChangeXColor = useMemo(() => {
    return (
      isFunction(handleChangeXColor) &&
      isOwner &&
      chartType &&
      [ChartType.DOUGHNUT, ChartType.PIE].includes(chartType)
    );
  }, [isOwner, chartType]);

  const canChangeYColor = useMemo(() => {
    return (
      isFunction(handleChangeYColor) &&
      isOwner &&
      chartType &&
      [ChartType.LINE, ChartType.BAR, ChartType.TREND_LINE].includes(chartType)
    );
  }, [isOwner, chartType]);

  const renderRowContent = () => {
    const elements = [] as any;

    for (let idx = 0; idx < maxSheetLength; idx++) {
      elements.push(
        <tr key={idx}>
          {sheet.yAxis.map(({ value, highlight, color }, yAxisIndex) => {
            const item = (
              sheet.data.find((_, dataIndex) => dataIndex === yAxisIndex) || []
            ).slice(1)[idx];

            return (
              <td
                style={{
                  color: highlight ? getTextColor(color) : "unset",
                  background: highlight ? color : "unset",
                  border: `1px solid ${
                    theme.palette.mode === ThemeMode.DARK
                      ? theme.palette.grey[300]
                      : "#e0e0e0"
                  } `,
                  textAlign: "left",
                  padding: "8px",
                }}
                key={`${(Math.random() + 1).toString(36).substring(7)}`}
              >
                {item}
              </td>
            );
          })}
        </tr>
      );
    }

    return elements;
  };

  const renderContent = () => {
    const elements = [] as any;

    for (let idx = 0; idx < maxSheetLength; idx++) {
      elements.push(
        <Box key={idx}>
          {sheet.yAxis.map(
            (
              { value, hide: yHide, color: yColor, highlight },
              yAxisIndex: number
            ) => {
              const item = (
                sheet.data.find((_, dataIndex) => dataIndex === yAxisIndex) ||
                []
              ).slice(1)[idx];

              const { hide: xHide, color: xColor } =
                sheet.xAxis.find((ele) => ele.value === item) || {};

              return (
                <Stack
                  direction="row"
                  style={{
                    color:
                      !yHide && highlight && isFunction(handleSelectRow)
                        ? getTextColor(yColor)
                        : "unset",
                    background:
                      !yHide && highlight && isFunction(handleSelectRow)
                        ? yColor
                        : "unset",
                    padding: "10px 20px",
                    minHeight: 47,
                    border: `1px solid ${
                      theme.palette.mode === ThemeMode.DARK
                        ? theme.palette.grey[300]
                        : "#e0e0e0"
                    } `,
                    whiteSpace: "nowrap",
                  }}
                  key={yAxisIndex}
                >

                  <Typography lineHeight="24px">{convertStringToNumber(item, true)?.toLocaleString('en-AU')}&nbsp;</Typography>

                  {yAxisIndex === 0 && !xHide && canChangeXColor && (
                    <ColorPicker
                      value={xColor}
                      onChange={(value) =>
                        debouncedHandleChangeXColor(item, `#${value.hex}`)
                      }
                      hideTextfield
                      disableAlpha
                      defaultValue="transparent"
                    />
                  )}
                </Stack>
              );
            }
          )}
        </Box>
      );
    }

    return elements;
  };

  if (rotate) {
    return (
      <Box>
        <table
          style={{
            borderCollapse: "collapse",
            width: "100%",
          }}
        >
          <thead>
            <tr>
              {sheet.yAxis.map(({ value, highlight, color }) => (
                <th
                  key={(Math.random() + 1).toString(36).substring(7)}
                  style={{
                    border: `1px solid ${
                      theme.palette.mode === ThemeMode.DARK
                        ? theme.palette.grey[300]
                        : "#e0e0e0"
                    } `,
                    textAlign: "left",
                    padding: "8px",
                    background: highlight ? color : "unset",
                    color: highlight ? getTextColor(color) : "unset",
                  }}
                >
                  {value}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{renderRowContent()}</tbody>
        </table>
      </Box>
    );
  }

  return (
    <Box
      style={{
        marginTop: 15,
        width: "100%",
        display: "inline-flex",
        transition: "box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
        borderRadius: "4px",
      }}
    >
      <Box>
        {sheet.yAxis.map(({ value, hide, color, highlight }, idx: number) => (
          <Stack
            onClick={() =>
              !hide && isFunction(handleSelectRow) && handleSelectRow(value)
            }
            direction="row"
            style={{
              color:
                !hide && highlight && isFunction(handleSelectRow)
                  ? getTextColor(color)
                  : "unset",
              background:
                !hide && highlight && isFunction(handleSelectRow)
                  ? color
                  : "unset",
              cursor:
                !hide && isFunction(handleSelectRow) ? "pointer" : "unset",
              minHeight: 47,
              padding: "10px 20px",
              border: `1px solid ${
                theme.palette.mode === ThemeMode.DARK
                  ? theme.palette.grey[300]
                  : "#e0e0e0"
              } `,
              whiteSpace: "nowrap",
            }}
            key={idx}
          >
            <Typography
              style={{
                fontWeight: "bold",
              }}
            >
              {value}
            </Typography>
            {!hide && canChangeYColor && (
              <ColorPicker
                value={color}
                onChange={(color) =>
                  debouncedHandleChangeYColor(value, `#${color.hex}`)
                }
                hideTextfield
                disableAlpha
                defaultValue="transparent"
              />
            )}
          </Stack>
        ))}
      </Box>
      <Box
        style={{
          overflowX: "scroll",
        }}
      >
        <Box
          style={{
            display: "flex",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Box>
  );
}
